/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    // Get source and destination from attributes or querySelector
    const sourceSelector = this.getAttribute('source');
    const destinationSelector = this.getAttribute('destination');
    
    if (sourceSelector) {
      this.source = document.querySelector(sourceSelector);
    }
    
    if (destinationSelector) {
      this.destination = document.querySelector(destinationSelector);
    }
    
    // Fallback: try to find cart button if destination not specified
    if (!this.destination) {
      this.destination = document.querySelector('.cart-drawer-button') || document.querySelector('[href*="/cart"]');
    }

    if (!this.source || !this.destination) {
      console.warn('FlyToCart: Source or destination not found', { source: this.source, destination: this.destination });
      this.remove();
      return;
    }

    // Get bounding rects immediately - if both are visible, animate right away
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();
    
    // Check if both elements are visible (have non-zero dimensions)
    const sourceVisible = sourceRect.width > 0 && sourceRect.height > 0;
    const destinationVisible = destinationRect.width > 0 && destinationRect.height > 0;
    
    if (sourceVisible && destinationVisible) {
      // Both elements are visible, animate immediately
      this.#animate(sourceRect, destinationRect);
    } else {
      // Use IntersectionObserver if elements aren't visible yet
      const intersectionObserver = new IntersectionObserver((entries) => {
        /** @type {DOMRectReadOnly | null} */
        let observedSourceRect = null;
        /** @type {DOMRectReadOnly | null} */
        let observedDestinationRect = null;

        entries.forEach((entry) => {
          if (entry.target === this.source) {
            observedSourceRect = entry.boundingClientRect;
          } else if (entry.target === this.destination) {
            observedDestinationRect = entry.boundingClientRect;
          }
        });

        if (observedSourceRect && observedDestinationRect) {
          this.#animate(observedSourceRect, observedDestinationRect);
          intersectionObserver.disconnect();
        }
      }, { threshold: 0.1 });
      
      intersectionObserver.observe(this.source);
      intersectionObserver.observe(this.destination);
      
      // Timeout fallback - if observer doesn't trigger within 500ms, try anyway
      setTimeout(() => {
        intersectionObserver.disconnect();
        const fallbackSourceRect = this.source.getBoundingClientRect();
        const fallbackDestinationRect = this.destination.getBoundingClientRect();
        if (fallbackSourceRect && fallbackDestinationRect) {
          this.#animate(fallbackSourceRect, fallbackDestinationRect);
        } else {
          this.remove();
        }
      }, 500);
    }
  }
  
  /**
   * Yield to main thread using scheduler API when available, with fallback
   */
  #yieldToMainThread() {
    if ('scheduler' in window && 'yield' in scheduler) {
      // @ts-ignore - TypeScript doesn't recognize the yield method yet
      return scheduler.yield();
    }

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 0);
      });
    });
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    // Clone the source image if it's an img element
    if (this.source && this.source.tagName === 'IMG') {
      const clonedImg = this.source.cloneNode(true);
      clonedImg.style.width = '100%';
      clonedImg.style.height = '100%';
      clonedImg.style.objectFit = 'cover';
      clonedImg.style.borderRadius = '4px';
      this.appendChild(clonedImg);
    } else if (this.source) {
      // If source is not an img, try to get background image or create a placeholder
      const computedStyle = window.getComputedStyle(this.source);
      const bgImage = computedStyle.backgroundImage;
      if (bgImage && bgImage !== 'none') {
        this.style.backgroundImage = bgImage;
        this.style.backgroundSize = 'cover';
        this.style.backgroundPosition = 'center';
      } else {
        // Create a placeholder div if no image found
        const placeholder = document.createElement('div');
        placeholder.style.width = '100%';
        placeholder.style.height = '100%';
        placeholder.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
        placeholder.style.borderRadius = '4px';
        this.appendChild(placeholder);
      }
    }

    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Add default class if none specified
    if (!this.classList.contains('fly-to-cart--main') && 
        !this.classList.contains('fly-to-cart--quick') && 
        !this.classList.contains('fly-to-cart--sticky')) {
      this.classList.add('fly-to-cart--sticky');
    }

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    } else {
      this.style.setProperty('--width', '40px');
      this.style.setProperty('--height', '40px');
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);
    this.style.setProperty('--start-opacity', '1');

    // Force a reflow to ensure CSS is applied before animation starts
    this.offsetHeight;
    
    await this.#yieldToMainThread();

    // Wait for animation to complete
    const animations = this.getAnimations();
    
    if (animations.length > 0) {
      await Promise.allSettled(animations.map((a) => a.finished));
    } else {
      // Fallback: wait for CSS animation duration (0.6s for main/quick, 0.8s for sticky)
      const duration = this.classList.contains('fly-to-cart--sticky') ? 800 : 600;
      await new Promise(resolve => setTimeout(resolve, duration));
    }
    
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}